// Import Packages
import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

// Import Recoil
import { useRecoilState, useSetRecoilState } from 'recoil';
import { alertState, atomSearchValue, operationLogsStatus } from '../../../recoil-store/recoil-state';

// Import Material UI
import { AppBar, Tabs, Tab, Box, Accordion, Tooltip, CircularProgress } from '@material-ui/core/';
import { Button, Grid } from "@material-ui/core";
import { makeStyles, withStyles } from '@material-ui/core/styles';

// Import Pages
import ProgressMonitor from './automMonitor/ProgressMonitor';
import PendingMonitor from './automMonitor/PendingMonitor';
import HistoryMonitor from './automMonitor/HistoryMonitor';
import ScheduleMonitor from './automMonitor/ScheduleMonitor';
import PropTypes from 'prop-types';
import QueueService from "../../../services/QueueService/QueueService";
import Search from "../../../components/Search/Search";
import Filters from '../../../components/DataTable/Filters/Filters';
import CustomizeView from "../../../components/DataTable/CustomizeView/CustomizeView";
import { OperationLogs } from './operationLogs/OperationLogs';
import { AllOperationLogs } from './operationLogs/AllOperationLogs';

// Import Icons
import emptyPageIcon from "../../../assets/icons/configurationIcons/empty-page.svg";
import automsGroupedShowIcon from "../../../assets/icons/configurationIcons/automs-grouped-show.svg";
import noQueuePoolShow from "../../../assets/icons/configurationIcons/no-queue-pool-show.svg";
import smartQueuePoolShow from "../../../assets/icons/configurationIcons/smart-queue-pool-show.svg";
import automListShow from "../../../assets/icons/configurationIcons/autom-list-show.svg";
import RefreshIcon from '@material-ui/icons/Refresh';

// Import Services
import QueuePoolService from '../../../services/QueuePoolService/QueuePoolService';
import OperationService from '../../../services/OperationService/OperationService';
import AutomService from '../../../services/AutomService/AutomService';

// Import CSS
import './MonitoringIndex.css';
import NewAccordion from '../../../components/Accordion/NewAccordion';
import DataTableLoader from '../../../components/ContentLoader/DataTableLoader';
import Pagination from '../../../components/Pagination/Pagination';
import { ContactsOutlined } from '@material-ui/icons';
import { getLocalItemsForPagination, getUserDetailFromJwt, setLocalItemsForPagination } from '../../../helpers/helper';
import { ProgressOperationLogs } from "./operationLogs/ProgressOperationLogs";
import { AllProgressOperationLogs } from "./operationLogs/AllProgressOperationLogs";


//override css material ui tabs 
const TabStyle = withStyles((theme) => ({
    root: {
        fontSize: "12px",
        fontWeight: "700",
        textTransform: "none",
        '&:hover': {
            color: 'var(--gravel) !important',
            opacity: 1,
        },
        '&$selected': {
            color: 'var(--black-pearl)',
        },
        minWidth: "180px",
    },
    selected: {},
}))((props) => <Tab {...props} />)

const useStyles = makeStyles(theme => ({
    customTabIndicator: {
        height: "3px",
        backgroundColor: "var(--black-pearl)"
    }
}));

function TabPanel(props) {
    const { children, value, index, showType, dataCount, automDataCount, fetchData, fetchQueueData, outPage, loading, ...other } = props;
    return (
        <div hidden={value !== index} {...other} style={{ height: "100%", overflow: "hidden" }}>
            {value === index && (<Box className='monitoring-content-box-container' >
                <>
                    {children}
                    {["singleView", "automListView"].includes(showType) && fetchData ?
                        <Pagination totalItems={showType === "singleView" ? dataCount : automDataCount} callFetch={showType === "singleView" ? fetchData : fetchQueueData}
                            showType={showType} outItemsPerPageLimit={[10, 25, 50, 100]} outItemsPerPage={() => getLocalItemsForPagination("monitoring")} outPage={outPage}
                            disabled={loading} /> : ""}
                </>
            </Box>)}
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.any.isRequired,
    value: PropTypes.any.isRequired
};

const numberOfRowsToFetch = 201;
function MonitoringIndex(props) {
    const { t } = useTranslation();
    const [value, setValue] = useState("HISTORY");
    const [searchOperationValue, setSearchOperationValue] = useRecoilState(atomSearchValue);
    const [logsStatus, setLogsStatus] = useRecoilState(operationLogsStatus);
    const [openCustomizeView, setOpenCustomizeView] = useState(false);
    const [customColumn, setCustomColumn] = useState([])
    const [searchData, setSearchData] = useState([])
    const [data, setData] = useState([]);
    const [dataCount, setDataCount] = useState(0);
    const [automDataCount, setAutomDataCount] = useState(0);
    const [searchQueueData, setSearchQueueData] = useState([])
    const [queueData, setQueueData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [disableTab, setDisableTab] = useState(false);
    const [isRetryInProgress, setIsRetryInProgress] = useState([]);
    const [searchKeyData, setSearchKeyData] = useState("");
    const [openFilters, setOpenFilters] = useState(false);
    const [outPage, setOutPage] = useState(null);
    const [openActivityLog, setOpenActivityLog] = useState(false);
    const [openProgressActivityLog, setOpenProgressActivityLog] = useState(false);
    const [openAllOperationLogs, setOpenAllOperationLogs] = useState(false);
    const [openAllProgressOperationLogs, setOpenAllProgressOperationLogs] = useState(false);
    const [selectedOperation, setSelectedOperation] = useState({});
    const [showType, setShowType] = useState(localStorage.getItem("monitoringShowType") === "shortQueue" ? "automListView" : localStorage.getItem("monitoringShowType") ? localStorage.getItem("monitoringShowType") : "singleView");
    const [currentPage, setCurrentPage] = useState({ status: false, page: 1 });
    const classes = useStyles();

    const retryRef = useRef([]);

    const setAlert = useSetRecoilState(alertState);
    useEffect(() => {
        const localItemsPerPage = getLocalItemsForPagination("monitoring")
        setDisableTab(true)
        if (showType === "singleView") {
            callFetchData(0, localItemsPerPage ? localItemsPerPage : 10)
        }
        else if (showType !== "singleView") {
            callQueueFetchData(0, localItemsPerPage ? localItemsPerPage : 10)
        }
    }, [searchOperationValue, showType])


    useEffect(() => {
        if (logsStatus.operation_id && showType === "singleView") {
            let copyData = [...searchData];
            const operation_id = logsStatus.operation_id;

            const i = copyData.findIndex(x => x.operation_id === operation_id);
            if (i !== -1 && logsStatus.status !== copyData[i].logs_status) {
                copyData[i].logs_status = logsStatus.status;
                setSearchData(copyData);
            }
        }
    }, [logsStatus])

    const fetchQueueData = async (status, startIndex, itemsPerPage) => {
        setLocalItemsForPagination("monitoring", itemsPerPage)

        setLoading(true);
        const res = showType !== "automListView" ?
            await (status
                ? status !== `["SCHEDULE"]` ? QueuePoolService.getMonitoringQuePool(status, searchOperationValue) : AutomService.getQueuePoolsByScheduler()
                : value !== "SCHEDULE"
                    ? QueuePoolService.getMonitoringQuePool(value === "HISTORY" ? '["COMPLETED","CANCELED", "FAILED"]' : [value], searchOperationValue)
                    : AutomService.getQueuePoolsByScheduler())
            :
            await AutomService.getMonitoringAutomsByStatus(status, startIndex, itemsPerPage, searchOperationValue);
        if (res?.status) {
            setQueueData(res.data);
            setSearchQueueData(res.data);
            setAutomDataCount(res.count)
        }
        else {
            setQueueData([]);
            setSearchQueueData([]);
            setAutomDataCount(0)
        }
        // setLoading(false);
    }

    const fetchQueueDetails = async (data, startIndex, itemsPerPage, fromRefresh) => {
        setOutPage(null)
        if (showType !== "automListView") {
            if (fromRefresh) {
                let i = searchQueueData.findIndex(x => x.qpool_id === data.qpool_id);
                if (!data.qpool_id) {
                    i = 0
                }
                if (i !== -1) {
                    const status = value === "HISTORY" ? '["COMPLETED","CANCELED", "FAILED"]' : [value];
                    const response = await QueuePoolService.getQueuePoolOperationCount(status, data.qpool_id ? data.qpool_id : "null", searchOperationValue);
                    if (!response?.status) {
                        return setAlert(alert => ({ ...alert, show: true, message: t('MESSAGES.MONITORING.REFRESH-ERROR'), severity: 'error' }))
                    }
                    else if (response.code === 404) {
                        setAlert(alert => ({ ...alert, show: true, message: t('MESSAGES.MONITORING.REFRESH-NOT-FOUND'), severity: 'warning' }))
                        let copyData = [...searchQueueData];
                        copyData[i].operation_count = 0;
                        setSearchQueueData(copyData);
                    }
                    else if (response.code === 200 && response.data?.length) {
                        let copyData = [...searchQueueData];
                        if (copyData[i].operation_count === response.data[0].operation_count) {
                            setAlert(alert => ({ ...alert, show: true, message: t('MESSAGES.MONITORING.REFRESH-SAME-COUNT'), severity: 'success' }))
                            return null
                        }
                        setAlert(alert => ({ ...alert, show: true, message: t('MESSAGES.MONITORING.REFRESH-SUCCESS'), severity: 'success' }))
                        copyData[i].operation_count = response.data[0].operation_count;
                        setSearchQueueData(copyData);
                    }
                }
                if (startIndex === -1 && itemsPerPage === -1) {
                    return null
                }
            }

            let res
            if (value === "SCHEDULE") {
                res = await AutomService.getNextRunTimesOfSchedulers(data.qpool_id ? data.qpool_id : "null", startIndex, itemsPerPage, searchOperationValue);
            }
            else {
                res = await OperationService.getQuePoolOperations(value === "HISTORY" ? '["COMPLETED","CANCELED", "FAILED"]' :
                    [value], data.qpool_id ? data.qpool_id : null, startIndex, itemsPerPage, searchOperationValue);
            }
            if (res?.status && res.data) {
                return { data: res.data, count: res.operation_count }
            }
            return null
        }
        else {
            // if (fromRefresh) {
            //     let i = searchQueueData.findIndex(x => x.qpool_id === data.qpool_id);
            //     if (i !== -1) {
            //         const status = value === "HISTORY" ? '["COMPLETED","CANCELED", "FAILED"]' : [value];
            //         const response = await QueuePoolService.getQueuePoolOperationCount(status, data.qpool_id ? data.qpool_id : "null");
            //         if (response.code === 200 && response.data.length) {
            //             let copyData = [...searchQueueData];
            //             copyData[i].operation_count = response.data[0].operation_count;
            //             setSearchQueueData(copyData);
            //         } else if (response.code === 404) {
            //             let copyData = [...searchQueueData];
            //             copyData[i].operation_count = 0;
            //             setSearchQueueData(copyData);
            //         }
            //     }
            //     if (startIndex === -1 && itemsPerPage === -1) {
            //         return null
            //     }
            // }

            let res
            if (value === "SCHEDULE") {
                res = await AutomService.getNextRunTimesOfSchedulers(data.qpool_id ? data.qpool_id : "null", startIndex, itemsPerPage, searchOperationValue);
            }
            else {
                res = await OperationService.getAutomOperations(value === "HISTORY" ? '["COMPLETED","CANCELED", "FAILED"]' :
                    [value], data.id ? data.id : null, startIndex, itemsPerPage, searchOperationValue);
            }
            if (res?.status && res.data) {
                if (fromRefresh) {
                    let i = searchQueueData.findIndex(x => x.id === data.id);
                    if (i !== -1) {
                        let copyData = [...searchQueueData];
                        if (copyData[i].operation_count === res.operation_count) {
                            setAlert(alert => ({ ...alert, show: true, message: t('MESSAGES.MONITORING.REFRESH-SAME-COUNT'), severity: 'success' }))
                            return null
                        }
                        setAlert(alert => ({ ...alert, show: true, message: t('MESSAGES.MONITORING.REFRESH-SUCCESS'), severity: 'success' }))
                        copyData[i].operation_count = res.operation_count;
                        setSearchQueueData(copyData);
                    }
                }
                return { data: res.data, count: res.operation_count }
            }
            setAlert(alert => ({ ...alert, show: true, message: t('MESSAGES.MONITORING.FETCH-CHILD-ERROR'), severity: 'error' }))
            return null
        }
    };

    const fetchData = async (val, startIndex, itemsPerPage) => {
        setLocalItemsForPagination("monitoring", itemsPerPage)
        if (value !== "SCHEDULE" || val !== '["SCHEDULE"]') {
            setLoading(true);
            let response = await OperationService.getMonitoringOperations(val, startIndex, itemsPerPage, searchOperationValue);
            if (response && value !== 3) {
                setData(response.data);
                setSearchData(response.data);
                setDataCount(response.operation_count)
            }
            else if (!response?.data?.length) {
                setData([]);
                setSearchData([]);
            }

            // setLoading(false);
        }
        else if (value === "SCHEDULE" || val === '["SCHEDULE"]') {
            await getNextRunTimesOfSchedulers()
        }
    };

    const getNextRunTimesOfSchedulers = async () => {
        const result = await AutomService.getNextRunTimesOfSchedulers(null, 0, null, searchOperationValue);
        if (result?.status) {
            setData(result.data);
            setSearchData(result.data);
        }
    }

    const handleRetryOperation = async (id) => {
        if (retryRef.current.includes(id)) {
            console.log(`Request already in progress for ID: ${id}`);
            return;
        }

        if (!retryRef.current.includes(id)) {
            retryRef.current.push(id);
        }

        // Get element by id
        const imgEl = document.getElementById(`retry-${id}`);

        // Disable retry action
        imgEl.style.cursor = 'not-allowed'
        imgEl.style.opacity = '0.6'

        setIsRetryInProgress([...retryRef.current])



        const result = await OperationService.retryOperation(id);
        retryRef.current = retryRef.current.filter((retryId) => retryId !== id);
        setIsRetryInProgress([...retryRef.current])
        if (result.status) {
            if (result.code === 204) {
                setAlert(alert => ({ ...alert, show: true, message: t('MESSAGES.OPERATION.' + result.message), severity: 'warning' }))
            } else {
                setAlert(alert => ({ ...alert, show: true, message: t('MESSAGES.OPERATION.' + result.message), severity: 'success' }))
                setSearchData((pre) => {
                    let copyData = [...pre];
                    const i = copyData.findIndex(x => x.operation_id === id);
                    if (i !== -1) {
                        if (copyData[i].operation_status === "IN-PROGRESS" || copyData[i].operation_status === "PENDING") {
                            copyData.splice(i, 1);
                        }
                        else {
                            copyData[i].operation_status = "COMPLETED";
                        }
                        return copyData
                    }
                    return pre
                });
            }
        } else {
            setAlert(alert => ({ ...alert, show: true, message: t('MESSAGES.OPERATION.' + result.message), severity: 'error' }))
        }

        // Enable retry action
        imgEl.style.cursor = 'pointer'
        imgEl.style.opacity = '1'
    }


    const callFetchData = async (startIndex, itemsPerPage) => {
        fetchData(`${value === "HISTORY" ? '["COMPLETED","CANCELED", "FAILED"]' : `["${value}"]`}`, startIndex, itemsPerPage).finally(() => {
            setLoading(false)
            setDisableTab(false)
            setOutPage(null)
        })
    };
    const callQueueFetchData = async (startIndex, itemsPerPage) => {
        fetchQueueData(`${value === "HISTORY" ? '["COMPLETED","CANCELED", "FAILED"]' : `["${value}"]`}`, startIndex, itemsPerPage).finally(() => {
            setLoading(false)
            setDisableTab(false)
            setOutPage(null)
        })
    };

    function handleDirectFetchQueueData() {
        const localItemsPerPage = getLocalItemsForPagination("monitoring")
        if (showType !== "singleView") {
            callQueueFetchData(0, localItemsPerPage ? localItemsPerPage : 10)
        }
    }


    function handleChangeType(value) {
        setShowType(value)
        localStorage.setItem("monitoringShowType", value)
        setOutPage(1)
    }
    function handleSelectOperation(data) {
        if (data.autom_id) {
            setOpenActivityLog(true)
            setSelectedOperation(data)
        }
    }
    function handleSelectProgressOperation(data) {
        if (data.autom_id) {
            setOpenProgressActivityLog(true)
            setSelectedOperation(data)
        }
    }

    function handleShowAllOperationLogs(data) {
        if (data.autom_id) {
            setOpenAllOperationLogs(true)
            setSelectedOperation(data)
        }
    }

    function handleShowAllProgressOperationLogs(data) {
        if (data.autom_id) {
            setOpenAllProgressOperationLogs(true)
            setSelectedOperation(data)
        }
    }

    const handleChange = (e, newValue) => {
        const localItemsPerPage = getLocalItemsForPagination("monitoring")
        if (newValue === value || disableTab) {
            return
        }
        // setSearchData([]);
        switch (newValue) {
            case "IN-PROGRESS":
                setDisableTab(true)
                setValue(newValue)
                const fetchInProgressData1 = fetchData('["IN-PROGRESS"]', 0, localItemsPerPage ? localItemsPerPage : 10, searchOperationValue);
                const fetchInProgressData2 = fetchQueueData('["IN-PROGRESS"]', 0, localItemsPerPage ? localItemsPerPage : 10);

                Promise.all([fetchInProgressData1, fetchInProgressData2])
                    .finally(() => {
                        setDisableTab(false)
                        setLoading(false);
                    })
                break;
            case "PENDING":
                setDisableTab(true)
                setValue(newValue)
                const fetchPendingData1 = fetchData('["PENDING"]', 0, localItemsPerPage ? localItemsPerPage : 10, searchOperationValue);
                const fetchPendingData2 = fetchQueueData('["PENDING"]', 0, localItemsPerPage ? localItemsPerPage : 10);

                Promise.all([fetchPendingData1, fetchPendingData2])
                    .finally(() => {
                        setDisableTab(false)
                        setLoading(false);
                    })
                break;
            case "HISTORY":
                setDisableTab(true)
                setValue(newValue)
                const fetchHistoryData1 = fetchData('["COMPLETED","CANCELED", "FAILED"]', 0, localItemsPerPage ? localItemsPerPage : 10, searchOperationValue);
                const fetchHistoryData2 = fetchQueueData('["COMPLETED","CANCELED", "FAILED"]', 0, localItemsPerPage ? localItemsPerPage : 10);
                Promise.all([fetchHistoryData1, fetchHistoryData2])
                    .finally(() => {
                        setDisableTab(false)
                        setLoading(false);
                    })
                break;
            case "SCHEDULE":
                if (showType === "automListView") {
                    setShowType("singleView")
                }
                setDisableTab(true)
                setValue(newValue)
                const fetchScheduleData1 = getNextRunTimesOfSchedulers();
                const fetchScheduleData2 = fetchQueueData('["SCHEDULE"]', 0, localItemsPerPage ? localItemsPerPage : 10).then(() => setValue(newValue));

                Promise.all([fetchScheduleData1, fetchScheduleData2])
                    .finally(() => {
                        setDisableTab(false)
                        setLoading(false);
                    })
                break;
            default:
                break;
        }
    };


    return (
        <div id="credential-container" className="data-container monitoring-container" style={{ height: "98%" }}>
            {/* <div className='header-div'>
                <h3 className='header-title'>{t('MESSAGES.MONITORING.MONITORING')}</h3>
            </div> */}
            <Search data={data} searchKey={searchKeyData} xs={11} sm={11} md={11} lg={5}
                xl={5} spacing={2} outClassName={"monitoring-search-bar"}>
            </Search>
            <Grid className="monitoring-tab-container">
                <Grid onClick={(e) => handleChange(e, "HISTORY")} className={`monitoring-tab-item ${value === "HISTORY" ? "monitoring-active-tab" : ""} `} style={{ opacity: `${disableTab && value !== "HISTORY" ? "0.5" : "1"}` }}>
                    <div>
                        <h2 className="monitoring-tab-title">
                            <span style={{ backgroundColor: "#D75E5E" }} className="monitoring-tab-dot"></span>
                            {t('MESSAGES.MONITORING.HISTORY')}
                        </h2>
                        <p className="monitoring-tab-description">{t('MESSAGES.MONITORING.HISTORY-DESCRIPTION')}</p>
                    </div>
                </Grid>
                <Grid onClick={(e) => handleChange(e, "IN-PROGRESS")} className={`monitoring-tab-item ${value === "IN-PROGRESS" ? "monitoring-active-tab" : ""} `} style={{ opacity: `${disableTab && value !== "IN-PROGRESS" ? "0.5" : "1"}` }} >
                    <div>
                        <h2 className="monitoring-tab-title">
                            <span style={{ backgroundColor: "#00AE31" }} className="monitoring-tab-dot"></span>
                            {t('MESSAGES.MONITORING.IN-PROGRESS')}
                        </h2>
                        <p className="monitoring-tab-description">{t('MESSAGES.MONITORING.IN-PROGRESS-DESCRIPTION')}</p>
                    </div>
                </Grid>
                <Grid onClick={(e) => handleChange(e, "PENDING")} className={`monitoring-tab-item ${value === "PENDING" ? "monitoring-active-tab" : ""} `} style={{ opacity: `${disableTab && value !== "PENDING" ? "0.5" : "1"}` }} >
                    <div>
                        <h2 className="monitoring-tab-title">
                            <span style={{ backgroundColor: "#FF891D" }} className="monitoring-tab-dot"></span>
                            {t('MESSAGES.MONITORING.PENDING')}
                        </h2>
                        <p className="monitoring-tab-description">{t('MESSAGES.MONITORING.PENDING-DESCRIPTION')}</p>
                    </div>
                </Grid>
                <Grid onClick={(e) => handleChange(e, "SCHEDULE")} className={`monitoring-tab-item ${value === "SCHEDULE" ? "monitoring-active-tab" : ""} `} style={{ opacity: `${disableTab && value !== "SCHEDULE" ? "0.5" : "1"}` }} >
                    <div>
                        <h2 className="monitoring-tab-title">
                            <span style={{ backgroundColor: "#3B8EE8" }} className="monitoring-tab-dot"></span>
                            {t('MESSAGES.MONITORING.SCHEDULED-WORK')}
                        </h2>
                        <p className="monitoring-tab-description">{t('MESSAGES.MONITORING.SCHEDULED-WORK-DESCRIPTION')}</p>
                    </div></Grid>
            </Grid>
            <div className="monitoring-area-container">
                <hr className="monitoring-line" />
                {loading ? <CircularProgress size={16} /> : <RefreshIcon onClick={(e) => {
                    const localItemsPerPage = getLocalItemsForPagination("monitoring")
                    setDisableTab(true)
                    if (showType === "singleView") {
                        callFetchData(0, localItemsPerPage ? localItemsPerPage : 10)
                    }
                    if (showType !== "singleView") {
                        callQueueFetchData(0, localItemsPerPage ? localItemsPerPage : 10)
                    }
                    setOutPage(1)
                }} className='monitoring-refresh-icon' />}
                <hr className="monitoring-line refresh-seperator" />
                <div className='filter-icons'>
                    {value !== "SCHEDULE" ? <Tooltip title={t('MESSAGES.MONITORING.AUTOM-LIST-SHOW')} placement="top">
                        <img onClick={(e) => handleChangeType("automListView")} style={{ opacity: `${showType !== "automListView" ? "0.5" : "1"} ` }} src={automListShow} />
                    </Tooltip > : <div style={{ width: "15px", height: "15px" }}></div>}
                    <Tooltip title={t('MESSAGES.MONITORING.SINGLE-SHOW')} placement="top">
                        <img onClick={(e) => handleChangeType("singleView")} style={{ opacity: `${showType !== "singleView" ? "0.5" : "1"} ` }} src={noQueuePoolShow} />
                    </Tooltip >
                    <Tooltip title={t('MESSAGES.MONITORING.QUE-POOL-SHOW')} placement="top">
                        <img onClick={(e) => handleChangeType("longQueue")} style={{ opacity: `${showType !== "longQueue" ? "0.5" : "1"} ` }} src={automsGroupedShowIcon} />
                    </Tooltip >
                    {/* <Tooltip title={t('MESSAGES.MONITORING.SMART-QUE-POOL-SHOW')} placement="top">
                        <img onClick={(e) => handleChangeType("automListView")} style={{ opacity: `${showType !== "automListView" ? "0.5" : "1"} ` }} src={smartQueuePoolShow} />
                    </Tooltip > */}
                </div>
            </div>
            {/* showType,dataCount,automDataCount,fetchData,fetchQueueData */}
            <TabPanel value={value} index={"IN-PROGRESS"} showType={showType} dataCount={dataCount} loading={loading}
                automDataCount={automDataCount} fetchData={callFetchData} fetchQueueData={callQueueFetchData} outPage={outPage} >
                <ProgressMonitor setSearchKeyData={setSearchKeyData}
                    handleSelectOperation={handleSelectOperation}
                    handleSelectProgressOperation={handleSelectProgressOperation}
                    handleRetryOperation={handleRetryOperation}
                    isRetryInProgress={isRetryInProgress}
                    outCurrentPage={currentPage}
                    setSearchData={setSearchData} customColumn={customColumn}
                    searchData={searchData} setCustomColumn={setCustomColumn}
                    searchQueueData={searchQueueData}
                    setSearchQueueData={setSearchQueueData}
                    showType={showType}
                    fetchQueueDetails={fetchQueueDetails}
                    fetchQueueData={handleDirectFetchQueueData}
                    dataCount={dataCount}
                    automDataCount={automDataCount}
                    loading={loading}
                    fetchData={callFetchData} /></TabPanel>
            <TabPanel value={value} index={"PENDING"} showType={showType} dataCount={dataCount} loading={loading}
                automDataCount={automDataCount} fetchData={callFetchData} fetchQueueData={callQueueFetchData} outPage={outPage}>
                <PendingMonitor setSearchKeyData={setSearchKeyData}
                    handleSelectOperation={handleSelectOperation}
                    handleRetryOperation={handleRetryOperation}
                    outCurrentPage={currentPage}
                    setSearchData={setSearchData} customColumn={customColumn}
                    searchData={searchData}
                    searchQueueData={searchQueueData}
                    setSearchQueueData={setSearchQueueData}
                    fetchData={callFetchData}
                    showType={showType}
                    fetchQueueDetails={fetchQueueDetails}
                    fetchQueueData={handleDirectFetchQueueData}
                    dataCount={dataCount}
                    automDataCount={automDataCount}
                    loading={loading}
                    setCustomColumn={setCustomColumn} /></TabPanel>
            <TabPanel value={value} index={"HISTORY"} showType={showType} dataCount={dataCount} loading={loading}
                automDataCount={automDataCount} fetchData={callFetchData} fetchQueueData={callQueueFetchData} outPage={outPage}>
                <HistoryMonitor setLoading={setLoading}
                    handleSelectOperation={handleSelectOperation}
                    handleRetryOperation={handleRetryOperation}
                    isRetryInProgress={isRetryInProgress}
                    outCurrentPage={currentPage}
                    setSearchKeyData={setSearchKeyData}
                    setSearchData={setSearchData} customColumn={customColumn}
                    searchData={searchData}
                    fetchData={callFetchData}
                    searchQueueData={searchQueueData}
                    setSearchQueueData={setSearchQueueData}
                    showType={showType}
                    fetchQueueDetails={fetchQueueDetails}
                    fetchQueueData={handleDirectFetchQueueData}
                    dataCount={dataCount}
                    automDataCount={automDataCount}
                    loading={loading}
                    setCustomColumn={setCustomColumn} /></TabPanel>
            <TabPanel value={value} index={"SCHEDULE"} showType={showType} dataCount={dataCount} loading={loading}
                automDataCount={automDataCount} fetchQueueData={callQueueFetchData} outPage={outPage}>
                <ScheduleMonitor loading={loading} setLoading={setLoading}
                    handleSelectOperation={handleSelectOperation}
                    outCurrentPage={currentPage}
                    setData={setData} setSearchKeyData={setSearchKeyData}
                    setSearchData={setSearchData} customColumn={customColumn}
                    searchData={searchData}
                    searchQueueData={searchQueueData}
                    setSearchQueueData={setSearchQueueData}
                    showType={showType}
                    fetchQueueDetails={fetchQueueDetails}
                    fetchQueueData={handleDirectFetchQueueData}
                    dataCount={dataCount}
                    automDataCount={automDataCount}
                    setCustomColumn={setCustomColumn} /></TabPanel>
            {openCustomizeView && <CustomizeView setOpen={setOpenCustomizeView} customColumn={customColumn}
                setCustomColumn={setCustomColumn} />}
            {openFilters &&
                <Filters setOpen={setOpenFilters} customColumn={customColumn} setCustomColumn={setCustomColumn} />}
            {openActivityLog &&
                <OperationLogs data={selectedOperation} setOpenActivityLog={setOpenActivityLog} setSelectedOperation={setSelectedOperation}
                    status={value} handleShowAllOperationLogs={handleShowAllOperationLogs} />}
            {openProgressActivityLog &&
                <ProgressOperationLogs data={selectedOperation} setOpenProgressActivityLog={setOpenProgressActivityLog} setSelectedOperation={setSelectedOperation}
                    status={value} handleShowAllProgressOperationLogs={handleShowAllProgressOperationLogs} />}
            {openAllOperationLogs &&
                <AllOperationLogs data={selectedOperation} setOpenActivityLog={setOpenAllOperationLogs} setSelectedOperation={setSelectedOperation}
                    status={value} handleShowAllOperationLogs={handleShowAllOperationLogs} />}
            {openAllProgressOperationLogs &&
                <AllProgressOperationLogs data={selectedOperation} setOpenActivityLog={setOpenAllProgressOperationLogs} setSelectedOperation={setSelectedOperation}
                    status={value} handleShowAllProgressOperationLogs={handleShowAllProgressOperationLogs} />}
        </div>
    );
}

export default MonitoringIndex;
